/**
 * Universal Search Button Component
 * Triggers the universal search modal
 */

import React, { useState } from 'react'
import { Search } from 'lucide-react'
import UniversalSearchModal from './UniversalSearchModal'
import clsx from 'clsx'

interface UniversalSearchButtonProps {
  className?: string
  onSearch?: (query: string, context: string) => void
}

const UniversalSearchButton: React.FC<UniversalSearchButtonProps> = ({ className, onSearch }) => {
  const [isModalOpen, setIsModalOpen] = useState(false)

  // Handle keyboard shortcut
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd/Ctrl + K to open search
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setIsModalOpen(true)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  return (
    <>
      <button
        onClick={() => setIsModalOpen(true)}
        className={clsx(
          'flex items-center space-x-1.5 px-2 py-1.5 rounded-lg border transition-all',
          'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800',
          'hover:border-slate-400 dark:hover:border-slate-500',
          'focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:focus:ring-blue-400/20',
          className
        )}
        title="Search (⌘K)"
      >
        <Search className="w-4 h-4 text-slate-500 dark:text-slate-400" />
        <kbd className="hidden sm:inline-flex items-center px-1.5 py-0.5 text-xs bg-slate-100 dark:bg-slate-700 rounded text-slate-500 dark:text-slate-400">
          {navigator.platform?.includes('Mac') ? '⌘K' : 'Ctrl+K'}
        </kbd>
      </button>

      <UniversalSearchModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSearch={onSearch}
      />
    </>
  )
}

export default UniversalSearchButton